(() => {
  const roots = document.querySelectorAll('[data-mattress-details]');

  const decodeText = (value) => {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = value || '';
    return decoder.value.replace(/\s+/g, ' ').trim();
  };

  const decimal = (value) => String(value || '').replace('.', ',');
  const paragraph = (heading, text) => `<p><strong>${heading}</strong>${text}</p>`;

  const formatDimensions = (value) =>
    String(value || '')
      .replace(/\s*[x×]\s*/gi, ' × ')
      .replace(/\./g, ',')
      .replace(/\s+/g, ' ')
      .trim();

  const findDimensions = (text) => {
    const match = text.match(
      /(?:Størrelse\s*:\s*)?(\d+(?:[.,]\d+)?\s*[x×]\s*\d+(?:[.,]\d+)?\s*[x×]\s*\d+(?:[.,]\d+)?)\s*cm/i
    );
    return match ? formatDimensions(match[1]) : '';
  };

  const findWeight = (text) => {
    const match = text.match(/Vægt\s*:?\s*(\d+(?:[.,]\d+)?)\s*(kg|g|gram)\b/i);
    if (!match) return '';
    return `${decimal(match[1])} ${match[2].toLowerCase() === 'kg' ? 'kg' : 'g'}`;
  };

  const findPeople = (title, text) => {
    const combined = `${title} ${text}`;
    const direct = combined.match(/(?:Perfekt\s+til\s+)?([12])\s*person(?:er)?/i);
    if (direct) return Number(direct[1]);
    if (/dobbelt/i.test(combined)) return 2;
    if (/single/i.test(combined)) return 1;
    return null;
  };

  const buildMattressData = (title, text) => {
    const dimensions = findDimensions(text);
    const dimensionParts = dimensions.match(/\d+(?:[.,]\d+)?/g) || [];
    const height = dimensionParts.length >= 3 ? dimensionParts[2] : '';
    const people = findPeople(title, text);
    const weight = findWeight(text);
    const hasWirelessPump = /trådløs(?:\s+genopladelig)?\s+pumpe|trådløs\s+elektrisk\s+pumpe/i.test(`${title} ${text}`);
    const hasFootPump = /indbygget\s+fodpumpe/i.test(text);
    const hasUniversalValve = /universalpumper|standard\s+luftmadras\s+med\s+ventil/i.test(text);
    const hasPillow = /indbygget\s+(?:hoved)?pude/i.test(text);
    const adjustable = /justerbar\s+fasthed/i.test(text);

    let inflationValue = 'Ventil';
    let inflationBody =
      paragraph('Enkel ventil.', ' Madrassen pustes op med en separat pumpe. Luk ventilen helt efter oppustning, så lufttrykket bevares gennem natten.') +
      paragraph('Tilpas fastheden.', ' Stop oppustningen, når madrassen støtter kroppen uden at føles helt hård.');

    if (hasWirelessPump) {
      inflationValue = 'Trådløs elektrisk pumpe';
      inflationBody =
        paragraph('Pumpen er integreret.', ' Den trådløse, genopladelige pumpe gør madrassen klar uden en separat pumpe eller adgang til en stikkontakt.') +
        paragraph('Nem at oplade.', ' Pumpen kan tages ud af madrassen og oplades separat, så den er klar til næste overnatning.');
    } else if (hasFootPump) {
      inflationValue = 'Indbygget fodpumpe';
      inflationBody =
        paragraph('Pumpes op uden ekstra udstyr.', ' Den indbyggede fodpumpe betyder, at du ikke skal medbringe en løs pumpe.') +
        paragraph('Du styrer selv fastheden.', ' Pump med foden, indtil madrassen føles behagelig, og luk derefter ventilen.');
    } else if (hasUniversalValve) {
      inflationValue = 'Universalventil';
      inflationBody =
        paragraph('Passer til universalpumper.', ' Ventilen kan bruges med almindelige pumper til luftmadrasser, så du frit kan vælge manuel eller elektrisk pumpe.') +
        paragraph('Hurtig tømning.', ' Åbn ventilen helt, og pres luften ud, mens madrassen foldes sammen.');
    }

    let comfortValue = 'Klassisk luftmadras';
    let comfortBody =
      paragraph('En enkel og funktionel soveplads.', ' Konstruktionen giver afstand til jorden og passer godt til camping, festival og overnattende gæster.') +
      paragraph('Fastheden kan tilpasses.', ' Lidt eftergivenhed giver normalt bedre støtte end en madras, der er pumpet helt hård.');

    if (hasPillow) {
      comfortValue = 'Indbygget pude';
      comfortBody =
        paragraph('Hovedenden er hævet.', ' Den indbyggede pude giver støtte til hoved og nakke og reducerer behovet for at pakke en separat pude.') +
        paragraph('Samlet soveløsning.', ' Pude og madras pustes op som én enhed, så opsætningen er hurtig og enkel.');
    } else if (adjustable) {
      comfortValue = 'Justerbar fasthed';
      comfortBody =
        paragraph('Tilpas madrassen til dig.', ' Luftmængden kan justeres, så du selv vælger mellem en blødere eller fastere liggeflade.') +
        paragraph('Høj konstruktion.', ` Den${height ? ` ${decimal(height)} cm` : ''} høje madras giver god afstand til gulv og jord og føles mere som en almindelig seng.`);
    }

    return {
      capacity: {
        value: people ? `${people} ${people === 1 ? 'person' : 'personer'}` : 'Ikke oplyst',
        body: people
          ? paragraph(
              people === 1 ? 'God plads til én person.' : 'Fælles soveplads til to.',
              people === 1
                ? ' Madrassens bredde er tilpasset én voksen og gør den nem at placere i et mindre telt eller gæsteværelse.'
                : ' Den brede liggeflade giver to personer én samlet madras uden en åbning mellem to separate madrasser.'
            ) + paragraph('Tjek pladsen før brug.', ' Sammenlign madrassens mål med teltets bundmål eller den ledige gulvplads.')
          : paragraph('Kapaciteten er ikke oplyst.', ' Brug madrassens mål til at vurdere, hvor mange der kan ligge komfortabelt.'),
      },
      size: {
        value: dimensions ? `${dimensions} cm` : 'Ikke oplyst',
        body: dimensions
          ? paragraph('Mål i oppustet tilstand.', ` Madrassen måler ${dimensions} cm. Målene gør det nemt at kontrollere pladsen i teltet eller hjemmet.`) +
            paragraph('Husk lidt luft omkring madrassen.', ' I et telt bør der også være plads til skrå teltvægge, tasker og adgang til indgangen.')
          : paragraph('Målene er ikke oplyst.', ' Kontrollér produktbeskrivelsen eller kontakt kundeservice, hvis madrassen skal passe på et bestemt sted.'),
      },
      height: {
        value: height ? `${decimal(height)} cm` : 'Ikke oplyst',
        body: height
          ? paragraph(`${decimal(height)} cm over underlaget.`, Number(String(height).replace(',', '.')) >= 30
              ? ' Den ekstra højde gør det lettere at komme op og ned og giver en mere sengelignende oplevelse.'
              : ' Højden giver god afstand til jorden uden at gøre madrassen unødigt stor at transportere.') +
            paragraph('Højden påvirker komforten.', ' En højere madras kan føles blødere, mens lufttrykket stadig bør justeres efter kropsvægt og sovestilling.')
          : paragraph('Liggehøjden er ikke oplyst.', ' Se produktbillederne og de samlede mål for at vurdere madrassens profil.'),
      },
      inflation: { value: inflationValue, body: inflationBody },
      weight: {
        value: weight || 'Ikke oplyst',
        body: weight
          ? paragraph('Transportvægt.', ` Madrassen vejer ${weight}. Det er relevant, når den skal bæres mellem bil, teltplads eller overnatningssted.`) +
            paragraph('Pakning og tilbehør.', ' Husk, at en eventuel separat pumpe og transporttaske kan lægge lidt til den samlede vægt.')
          : paragraph('Vægten er ikke oplyst.', ' Hvis lav transportvægt er afgørende, anbefaler vi at få den præcise vægt bekræftet før køb.'),
      },
      comfort: { value: comfortValue, body: comfortBody },
    };
  };

  const buildPumpData = (text) => {
    const flow = text.match(/op\s+til\s+(\d+)\s*liter\s+luft\s+i\s+minuttet/i);
    const chargeTime = text.match(/Oplades\s+på\s+cirka\s+(\d+(?:[.,]\d+)?)\s*time/i);
    const noise = text.match(/(\d+)\s*dB/i);
    const weight = findWeight(text);
    const bothDirections = /både\s+puste\s+og\s+suge\s+luft\s+ud/i.test(text);
    const usb = /USB\s*-?\s*C/i.test(text);
    const battery = /indbygget\s+batteri|genopladeligt\s+batteri/i.test(text);

    return {
      capacity: {
        value: flow ? `${flow[1]} L/min.` : 'Ikke oplyst',
        body: flow
          ? paragraph('Høj luftmængde.', ` Pumpen flytter op til ${flow[1]} liter luft i minuttet og er udviklet til hurtig oppustning af luftmadrasser.`) +
            paragraph('Tiden afhænger af størrelsen.', ' En stor dobbeltmadras kræver mere luft end en singlemadras, men den høje luftmængde reducerer ventetiden.')
          : paragraph('Ydeevnen er ikke oplyst.', ' Oppustningstiden afhænger af madrassens volumen og den anvendte ventil.'),
      },
      size: {
        value: bothDirections ? 'Puster og tømmer' : 'Puster luft ind',
        body: bothDirections
          ? paragraph('To funktioner i én pumpe.', ' Pumpen kan både fylde madrassen med luft og suge luften ud igen, så den bliver lettere at folde kompakt.') +
            paragraph('Skift mundstykke efter ventilen.', ' De medfølgende mundstykker gør pumpen anvendelig til flere typer madrasser og oppusteligt udstyr.')
          : paragraph('Udviklet til oppustning.', ' Pumpen leverer en stabil luftstrøm til luftmadrasser og andet udstyr med en kompatibel ventil.') +
            paragraph('Vælg det rigtige mundstykke.', ' En tæt forbindelse mellem pumpe og ventil giver den hurtigste oppustning.'),
      },
      height: {
        value: battery ? 'Indbygget batteri' : 'Ikke oplyst',
        body: battery
          ? paragraph('Trådløs i brug.', ' Det indbyggede batteri betyder, at pumpen kan bruges ved teltet uden adgang til strøm eller løse batterier.') +
            paragraph('Oplad før turen.', ' En fuld opladning giver den mest stabile ydelse og sikrer, at pumpen er klar ved ankomst.')
          : paragraph('Strømforsyningen er ikke oplyst.', ' Kontrollér produktbeskrivelsen før brug væk fra en stikkontakt.'),
      },
      inflation: {
        value: usb ? `USB-C${chargeTime ? ` · ca. ${decimal(chargeTime[1])} time` : ''}` : 'Ikke oplyst',
        body: usb
          ? paragraph('Genopladning med USB-C.', ` Pumpen oplades med det medfølgende kabel${chargeTime ? ` på cirka ${decimal(chargeTime[1])} time` : ''}.`) +
            paragraph('Nem at oplade på farten.', ' USB-C gør det muligt at bruge en almindelig oplader eller kompatibel powerbank.')
          : paragraph('Opladningen er ikke oplyst.', ' Se den medfølgende vejledning for korrekt opladning.'),
      },
      weight: {
        value: noise ? `${noise[1]} dB` : 'Ikke oplyst',
        body: noise
          ? paragraph('Støjsvag for en elektrisk pumpe.', ` Det oplyste støjniveau er ${noise[1]} dB. Pumpen kan stadig høres, men oppustningen er normalt hurtigt overstået.`) +
            paragraph('Brug den med fri luft omkring.', ' Placér pumpen, så luftindtaget ikke dækkes, da det giver bedre ydelse og mindre belastning.')
          : paragraph('Støjniveauet er ikke oplyst.', ' Elektriske pumper afgiver lyd under brug, men oppustningen tager normalt kun kort tid.'),
      },
      comfort: {
        value: weight || 'Ikke oplyst',
        body: weight
          ? paragraph('Let at pakke med.', ` Pumpen vejer ${weight} og fylder kun lidt sammen med madrassen.`) +
            paragraph('Én pumpe til flere produkter.', ' Med de rigtige mundstykker kan den bruges til flere luftmadrasser og andet kompatibelt udstyr.')
          : paragraph('Vægten er ikke oplyst.', ' Hvis vægt og pakkevolumen er afgørende, bør de præcise mål bekræftes før køb.'),
      },
    };
  };

  roots.forEach((root) => {
    const source = root.querySelector('[data-mattress-product-data]');
    if (!source) return;

    let product;
    try {
      product = JSON.parse(source.textContent);
    } catch (error) {
      return;
    }

    const title = decodeText(product.title);
    const text = decodeText(product.description);
    const kind = root.dataset.productKind;
    const data = kind === 'pump' ? buildPumpData(text) : buildMattressData(title, text);

    root.querySelectorAll('[data-mattress-detail]').forEach((item) => {
      const detail = data[item.dataset.mattressDetail];
      if (!detail) return;
      item.querySelector('[data-detail-value]').textContent = detail.value;
      item.querySelector('[data-detail-body]').innerHTML = detail.body;
    });

    const details = root.querySelectorAll('details');
    details.forEach((detail) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open) return;
        details.forEach((other) => {
          if (other !== detail) other.open = false;
        });
      });
    });

    root.hidden = false;
  });
})();
